export type PaginationRangeItem = number | "ellipsis";

/**
 * Which page links to show around `currentPage`, MUI/Mantine-style: the number
 * of rendered slots is constant for a given config no matter where the current
 * page sits, so the row never grows and reflows (or overflows a tight column)
 * while the user pages. Near the edges the ellipsis slot is filled by the real
 * page number instead — a "…" hiding exactly one page would be wider than the
 * page itself. `siblingCount` is the pages kept on each side of the current
 * one, `boundaryCount` the pages pinned at each end.
 */
export function getPaginationRange(
    currentPage: number,
    pageCount: number,
    { siblingCount = 1, boundaryCount = 1 } = {},
): PaginationRangeItem[] {
    if (pageCount <= 0) {
        return [];
    }

    const page = Math.min(Math.max(currentPage, 1), pageCount);
    const range = (start: number, end: number) =>
        Array.from({ length: end - start + 1 }, (_, index) => start + index);

    // Sibling window + current page + both boundaries + the two ellipsis slots.
    const totalSlots = 2 * siblingCount + 2 * boundaryCount + 3;

    if (pageCount <= totalSlots) {
        return range(1, pageCount);
    }

    const siblingsStart = Math.max(
        Math.min(
            page - siblingCount,
            pageCount - boundaryCount - 2 * siblingCount - 1,
        ),
        boundaryCount + 2,
    );
    const siblingsEnd = Math.min(
        Math.max(page + siblingCount, boundaryCount + 2 * siblingCount + 2),
        pageCount - boundaryCount - 1,
    );

    return [
        ...range(1, boundaryCount),
        siblingsStart > boundaryCount + 2
            ? ("ellipsis" as const)
            : boundaryCount + 1,
        ...range(siblingsStart, siblingsEnd),
        siblingsEnd < pageCount - boundaryCount - 1
            ? ("ellipsis" as const)
            : pageCount - boundaryCount,
        ...range(pageCount - boundaryCount + 1, pageCount),
    ];
}
